import React, { useRef, useEffect } from 'react';
import { useDragStore } from '../../../stores/dragStore';

/* eslint-disable react/no-array-index-key */
interface TimelineItem {
  date: string;
  title: string;
  description: string;
}

const timelineItems: TimelineItem[] = [
  {
    date: 'November 2029',
    title: 'Laura v3.0 - AGI Breakthrough',
    description:
      'Achieved unprecedented reasoning capabilities and general problem-solving across multiple domains. V3 planned for public release in early 2030 with enhanced safety and alignment features.',
  },
  {
    date: 'June 2029',
    title: 'Series B Funding - $500M',
    description:
      'Closed Series B funding round led by major tech investors. Expanded research team and launched beta programs across financial services and healthcare.',
  },
  {
    date: 'February 2029',
    title: 'Laura v2.0 - Multimodal Intelligence',
    description:
      "Everything in this is pure BS. BTW there's no need to go to HR to re-activate your badge, that's a big pain. You see the squares at the bottom? Believe it or not, you can drag and drop them to other files.",
  },
  {
    date: 'September 2028',
    title: 'Series A Funding - $150M',
    description:
      'Announced Series A funding round to accelerate core research and infrastructure development. Established regional offices in Asia and Europe.',
  },
  {
    date: 'May 2028',
    title: 'Laura v1.5 - Enhanced Learning',
    description:
      'Introduced continuous learning capabilities and improved context understanding. First enterprise partnerships announced.',
  },
  {
    date: 'January 12th, 2027',
    title: 'Laura.ai Founded - Genesis',
    description:
      'Laura AI platform created with focus on artificial general intelligence. Initial research team assembled to tackle fundamental AGI challenges.',
  },
];

const renderStringWithCondition = (str: string) => {
  return str.split('').map((char, index) => {
    let bgColor = '';

    if (char === '2') bgColor = 'bg-red-500';
    else if (char === '0') bgColor = 'bg-blue-500';
    else if (char === '1') bgColor = 'bg-green-500';
    else if (char === '7') bgColor = 'bg-slate-500';

    return bgColor ? (
      <span key={index} className={`${bgColor} text-white`}>
        {char}
      </span>
    ) : (
      <span key={index}>{char}</span>
    );
  });
};

const TIMELINE_COLORS = ['bg-red-500', 'bg-blue-500', 'bg-green-500'];

export function TimeLine() {
  const squareRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { startDrag, registerTimelineSquarePosition } = useDragStore();

  const handleMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number,
    color: string,
  ) => {
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();

    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    startDrag(
      index,
      color,
      e.clientX - offsetX,
      e.clientY - offsetY,
      offsetX,
      offsetY,
    );
  };

  useEffect(() => {
    const updateSquarePositions = () => {
      squareRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          registerTimelineSquarePosition(
            index,
            rect.left,
            rect.top,
            rect.width,
            rect.height,
          );
        }
      });
    };

    // Register positions on mount
    updateSquarePositions();

    // Update positions on window resize
    window.addEventListener('resize', updateSquarePositions);
    return () => window.removeEventListener('resize', updateSquarePositions);
  }, [registerTimelineSquarePosition]);

  return (
    <article className="prose">
      <ul className="relative border-s border-gray-300">
        {timelineItems.map((item, index) => (
          <div
            key={item.date}
            className={index < timelineItems.length - 1 ? 'mb-10 ms-4' : 'ms-4'}
          >
            <div className="absolute w-3 h-3 bg-gray-400 rounded-full mt-1.5 -start-1.5 border border-gray-300" />
            <span className="text-sm font-normal leading-none text-body">
              {renderStringWithCondition(item.date)}
            </span>
            <h3 className="text-lg font-semibold text-heading my-2">
              {item.title}
            </h3>
            <p
              className={
                index === 0
                  ? 'mb-4 text-base font-normal text-body'
                  : 'text-base font-normal text-body'
              }
            >
              {item.description}
            </p>
          </div>
        ))}
      </ul>
      <div className="flex justify-around items-center gap-4 mt-20 px-4 md:px-0">
        {TIMELINE_COLORS.map((color, index) => (
          <div
            key={color}
            ref={(el) => {
              squareRefs.current[index] = el;
            }}
            role="button"
            tabIndex={0}
            aria-label={`Draggable colored square ${index + 1}`}
            onMouseDown={(e) => handleMouseDown(e, index, color)}
            className={`w-5 h-5 ${color} active:cursor-grabbing transition-all`}
          />
        ))}
      </div>
    </article>
  );
}
